import { util } from "./util";
import { Cache } from "./Cache";
import { XMLHttpRequestProcessed } from "@otto-ec/global-resources";
import { sendEventToTracking, SquirrelEvents } from "./tracking";
import { templates } from "./templates";
import { MARK, HEADLINE, SELECTOR, DATA, KEY_CODE, encode, getNextActiveElement, CLASSNAME } from "./helper";
import { suggestTracking } from "./suggestTracking";
import { AttributeResponse, Keyword, Suggestions, isKeywordsJSON } from "./helper";
import Mustache from "mustache";

export const TEMPLATE_NAME = "searchSuggest";

let headerElement: Element | null;
let searchbarWrapper: HTMLDivElement | null;
let searchForm: HTMLFormElement | null;
let searchField: HTMLInputElement | null;
let suggestLayer: HTMLDivElement | null;
let oldSearchTerm: string | undefined | null;
let renderCache: Cache<Promise<string>> | null;

let isEmptyState: boolean;
let headline: string;

/**
 *
 * @param string
 */
export function setOldSearchTerm(string: string) {
  oldSearchTerm = string;
}

export function focusOutSearchFieldAction(evt: Event) {
  if (performance.getEntriesByName(MARK.EMPTY_STATE_TIME_START).length > 0) {
    performance.measure(MARK.EMPTY_STATE_TIME, MARK.EMPTY_STATE_TIME_START);
  }
}

/**
 * Measure duration between sessionstart and currenttime
 * To get the sessionstart timestamp we are using the navigation start time.
 */
export function initialMeasureSuggestBoxTime() {
  const suggestBoxTimePerformance: PerformanceEntryList = performance.getEntriesByName(MARK.INITIAL_SEARCH_BOX_TIME);
  const sessionStart = Number(window.o_util.cookie.get("o_squirrel_sessionStart"));
  if (suggestBoxTimePerformance.length == 0 && !!sessionStart) {
    performance.measure(MARK.INITIAL_SEARCH_BOX_TIME, {
      start: sessionStart,
      end: new Date().getTime(),
    });
  }
}

/**
 * Updates the suggest-layer if the search field obtains focus. Needed after click on arrow.
 *
 * @param evt
 */
export function focusSearchFieldAction(evt: Event) {
  if (!headerElement) {
    throw new Error("searchSuggest.focusSearchFieldAction: headerElement is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.focusSearchFieldAction: suggestLayer is not defined");
  }

  performance.mark(MARK.SUGGEST_TIME_START);

  initialMeasureSuggestBoxTime();

  const selectedSearchField: HTMLInputElement = evt.target as HTMLInputElement;
  const value = selectedSearchField.value;
  const searchTerm: string = util.string.trimLeft(value);
  if (searchTerm !== "") {
    if (suggestLayer.innerHTML !== "" && !!oldSearchTerm) {
      showSuggestions(searchTerm);
    }
  } else {
    showEmptyState();
  }
}

/**
 * Used for updating the search field on arrows
 *
 * @param evt
 */
export function clickArrowAction(this: Element, evt: Event) {
  if (!searchField) {
    throw new Error("searchSuggest.clickArrowAction: searchField is not defined");
  }
  if (!searchForm) {
    throw new Error("searchSuggest.clickArrowAction: searchForm is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.parentNode).valueOf() ? this.parentNode : elem.offsetParent) as ParentNode; // different structure in IE8
  const term: HTMLSpanElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLSpanElement;
  const searchTerm: string | null = term.getAttribute(DATA.TERM);
  const position: string | null = term.getAttribute(DATA.POS);

  window.o_util.event.stop(evt);

  setSuggest(position);
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM) + " ");

  sendEventToTracking({
    san_SuggestAdoptTerm: searchTerm,
    san_SuggestAdoptPosition: position,
  });

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);

  searchField.focus();
}

/**
 *
 * @param evt
 */
export function hideLayerEventHandler(evt: MouseEvent) {
  const elem: HTMLElement = evt.target as HTMLElement;
  if (!elem.classList.contains(CLASSNAME.IGNORE_CLOSE_SUGGEST)) {
    hideLayer();
  }
}

/**
 *
 * @param suggestions
 */
export function updateSuggestionLayer<E extends HTMLDivElement>(this: E, suggestions: string): E {
  this.innerHTML = suggestions;
  return this;
}

/**
 * Click on suggest entry
 *
 * @param evt
 */
export function clickSearchTermAction(this: Element, evt: Event) {
  if (!searchForm) {
    throw new Error("searchSuggest.clickSearchTermAction: searchForm is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.clickSearchTermAction: searchField is not defined");
  }

  const elem: HTMLElement = evt.target as HTMLElement;
  const target: ParentNode = (Boolean(this.querySelector).valueOf() ? this : elem.offsetParent) as ParentNode;
  const term: HTMLDivElement = target.querySelector(SELECTOR.SEARCH_TERM) as HTMLDivElement;

  window.o_util.event.stop(evt);

  setSuggest(term.getAttribute(DATA.POS));
  const originalSearchTerm: AttributeResponse = searchField.getAttribute(DATA.SUGGEST_SEARCH_TERM);
  replaceSearchTermAndSetDataAttribute(term.getAttribute(DATA.TERM));
  const words: AttributeResponse = searchForm.getAttribute(DATA.SUGGEST_WORD_LIST);
  const wordLengths: AttributeResponse = searchForm.getAttribute(DATA.SUGGEST_WORD_LIST_COUNT);

  const performanceMeasure: PerformanceMeasure = performance.measure(MARK.SUGGEST_TIME, MARK.SUGGEST_TIME_START);
  const suggestTime: number = parseInt(performanceMeasure.duration.toString());

  const isSuggest = !!originalSearchTerm;

  suggestTracking.trackUsage(
    isSuggest
      ? {
          "wk.san_SuggestTime": suggestTime,
        }
      : {},
    false,
    true,
    words,
    wordLengths,
    term.getAttribute(DATA.POS),
    originalSearchTerm
  );

  suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_MODIFIED, searchForm);
  submitForm();
}

/**
 *
 * @param json
 */
export function extractSuggestions(json: string | Record<string, unknown> | undefined): 0 | Suggestions {
  if (!json || !isKeywordsJSON(json)) {
    throw new TypeError("searchSuggest.extractSuggestions invalid json format");
  }

  const keywords: Keyword[] = json.keywords;
  return (
    keywords.length && {
      keywords: keywords,
      suggestPos: util.core.counter(),
    }
  );
}

/**
 *
 * @param searchTerm
 */
export function fetchSuggestions(searchTerm: string): Promise<XMLHttpRequestProcessed> {
  if (!suggestLayer) {
    throw new Error("searchSuggest.fetchSuggestions: suggestLayer is not defined");
  }

  const url: string | null = suggestLayer.getAttribute(DATA.SUGGEST_URL);
  if (!url) {
    throw new Error("searchSuggest.fetchSuggestions: undefined suggest url");
  }

  return window.o_util.ajax.getJSON(encode(url, { q: searchTerm }));
}

/**
 * Promises rendered suggestion layer for the given search term.
 *
 * @param searchTerm the search term
 * @return rendered suggestion layer
 */
export function retrieveSuggestions(searchTerm: string): Promise<string> {
  return Promise.all([fetchSuggestions(searchTerm).then(suggestionResponseHandler), templates.get(TEMPLATE_NAME)]).then(
    renderSuggestions
  );
}

/**
 *
 */
export function closeLayer() {
  window.o_global.events<SquirrelEvents>().emit("squirrel.search.close");
  hideLayer();
}

/**
 *
 */
export function showLayer(): HTMLFormElement {
  if (!searchForm) {
    throw new Error("searchSuggest.showLayer: suggestLayer is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.showLayer: searchWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.showLayer: suggestLayer is not defined");
  }

  document.body.addEventListener("click", hideLayerEventHandler);
  searchbarWrapper.classList.add(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer.style.display = "block";
  return searchForm;
}

/**
 * Used when clicking on suggest to avoid sending get parameters
 */
export function submitForm() {
  if (!searchForm) {
    throw new Error("searchSuggest.submitForm: searchForm is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.submitForm: searchField is not defined");
  }

  const searchFieldValue: string = util.string.escapeSpecials(searchField.value);
  const action: string = searchForm.getAttribute("action") + "/" + encodeURIComponent(searchFieldValue) + "/";

  util.global.setLocation(action);
}

/**
 * Update suggest position as global information
 *
 * @param suggestPos
 * @param redirectTarget
 */
export function setSuggest(suggestPos?: string | number | undefined | null, redirectTarget?: string | null) {
  if (!searchForm) {
    throw new Error("searchSuggest.setSuggest: searchForm is not defined");
  }

  searchForm.setAttribute(DATA.POS, suggestPos ? (suggestPos as string) : "");
  if (redirectTarget) {
    searchForm.setAttribute(DATA.TARGET, redirectTarget ? redirectTarget : "");
  } else {
    searchForm.removeAttribute(DATA.TARGET);
  }
}

/**
 * Update text within search field and send spell check tracking
 *
 * @param searchTerm
 */
export function replaceSearchTerm(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTerm: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
  }
}

/**
 * Update text within search field and sets data attribute
 *
 * @param searchTerm
 */
export function replaceSearchTermAndSetDataAttribute(searchTerm: string | null) {
  if (!searchField) {
    throw new Error("searchSuggest.replaceSearchTermAndSetDataAttribute: searchField is not defined");
  }
  if (searchTerm !== "" && searchTerm !== null) {
    searchField.value = searchTerm;
    searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, util.string.trimRight(searchTerm));
  }
}

/**
 *
 * @param xhr
 */
export function suggestionResponseHandler(xhr: XMLHttpRequestProcessed): Suggestions | Promise<unknown> {
  return xhr.status === 200
    ? extractSuggestions(xhr.responseJSON) || Promise.reject(new Error("No suggestions to show"))
    : Promise.reject(new Error("received status code " + xhr.status));
}

/**
 * Renders the suggestions with the given template.
 *
 * @param results the input data
 */
export function renderSuggestions(results: [unknown, unknown]): string {
  const template = results[1] + "";
  const view = Object.assign(results[0], { isEmptyState, headline });
  return Mustache.render(template, view);
}

/**
 * Find and show suggestions for given searchTerm. Checks cache before performing an ajax request.
 *
 * @param searchTerm
 */
export function suggest(searchTerm: string) {
  if (!renderCache) {
    throw new Error("searchSuggest.suggest: renderCache is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.suggest: suggestLayer is not defined");
  }
  isEmptyState = searchTerm == "";
  const suggestions = renderCache.get(searchTerm).then(window.o_util.core.bind(suggestLayer, updateSuggestionLayer));

  setSuggest(-1);
  suggestions.then(showLayer).then(suggestTracking.writeTrackingSuggestions).then(undefined, hideLayer); // equivalent to catch(hidelayer) - looking at you jslint
}

/**
 * Used when pressing arrow down key
 * @param activeElement
 */
export function updateSearchField(activeElement: Element) {
  if (!searchForm) {
    throw new Error("searchSuggest.updateSearchField: searchForm is not defined");
  }
  const termElement: HTMLElement = activeElement.querySelector(SELECTOR.SEARCH_TERM) as HTMLElement;
  const redirectTarget: AttributeResponse = termElement.getAttribute(DATA.TARGET);
  const term: AttributeResponse = termElement
    ? termElement.getAttribute(DATA.TERM)
    : activeElement.getAttribute(DATA.TERM);
  const isContentSuggest = !!redirectTarget;
  if (Boolean(termElement).valueOf()) {
    setSuggest(termElement.getAttribute(DATA.POS), isContentSuggest ? redirectTarget : null);
  }
  replaceSearchTerm(term);
  suggestTracking.markFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
}

/**
 *
 * @param previous
 * @param next
 */
export function updateActiveElement(previous: HTMLLIElement | null, next: Element | null) {
  if (previous) {
    previous.classList.remove(CLASSNAME.SELECTED_SEARCH_SUGGEST);
  }
  if (next) {
    next.classList.add(CLASSNAME.SELECTED_SEARCH_SUGGEST);
    updateSearchField(next);
  }
}

/**
 *
 * @param key
 */
export function getInitiallyActiveElement(key: string): HTMLLIElement | null {
  if (!suggestLayer) {
    throw new Error("searchSuggest.getInitiallyActiveElement: suggestLayer is not defined");
  }

  const entries: NodeListOf<HTMLLIElement> = suggestLayer.querySelectorAll(".squirrel_searchSuggest");
  switch (key) {
    case KEY_CODE.ARROW_DOWN:
      return entries[0];
    case KEY_CODE.ARROW_UP:
      return entries[entries.length - 1];
    default:
      return null;
  }
}

export function onFirstChar() {
  if (!searchField) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }

  if (!oldSearchTerm && !!searchField.value) {
    performance.mark(MARK.SUGGEST_TIME_START);
    if (performance.getEntriesByName(MARK.EMPTY_STATE_TIME_START).length > 0) {
      performance.measure(MARK.EMPTY_STATE_TIME, MARK.EMPTY_STATE_TIME_START);
    }
  }
}

/**
 * Retrieve key depending on browser type and call the associated action.
 *
 * Hint: In the past key 229 (unused code) was skipped, but due to errors the skip was
 * removed and replaced by searchField.value !== "". Now the suggest will be opened correctly.
 *
 * @param evt
 */
export function handleKeyAction(evt: KeyboardEvent) {
  if (!searchForm) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }
  if (!searchField) {
    throw new Error("searchSuggest.handleKeyAction: searchField is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.handleKeyAction: suggestLayer is not defined");
  }
  if (!headerElement) {
    throw new Error("searchSuggest.handleKeyAction: headerElement is not defined");
  }

  let searchTerm: string;
  let activeElement: HTMLLIElement;
  const key: string = Boolean(evt.which).valueOf() ? evt.which + "" : evt.code;
  switch (key) {
    case KEY_CODE.ARROW_UP:
    case KEY_CODE.ARROW_DOWN:
      evt.preventDefault();
      activeElement = suggestLayer.querySelector(SELECTOR.SELECTED_SEARCH_SUGGEST) as HTMLLIElement;
      updateActiveElement(
        activeElement,
        activeElement ? getNextActiveElement(key, activeElement) : getInitiallyActiveElement(key)
      );
      break;
    case KEY_CODE.ESC:
      closeLayer();
      break;
    default:
      onFirstChar();
      searchTerm = util.string.trimLeft(searchField.value);
      searchField.setAttribute(DATA.SUGGEST_SEARCH_TERM, searchTerm);
      // Do not re-suggest on empty terms or form submits (enter)
      if (searchTerm !== "" && key !== KEY_CODE.ENTER) {
        if (oldSearchTerm !== searchTerm) {
          showSuggestions(searchTerm);
        }
        suggestTracking.unmarkFormForTracking(CLASSNAME.TRACK_SUGGEST, searchForm);
      } else {
        showEmptyState();
      }
      oldSearchTerm = searchTerm;
  }
}

function showSuggestions(searchTerm: string) {
  setHeadline(HEADLINE.SUGGESTIONS);
  suggest(searchTerm);
}

function showEmptyState() {
  if (window.o_squirrel.enableEmptyState) {
    performance.mark(MARK.EMPTY_STATE_TIME_START);
    setHeadline(HEADLINE.EMPTY_STATE);
    suggest("");
  } else {
    hideLayer();
  }
}

function setHeadline(label: string) {
  headline = label;
}

/**
 * Hide and Reset
 */
export function hideLayer() {
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.hideLayer: searchbarWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.hideLayer: suggestLayer is not defined");
  }
  if (!renderCache) {
    throw new Error("searchSuggest.hideLayer: renderCache is not defined");
  }
  document.body.removeEventListener("click", hideLayerEventHandler);
  searchbarWrapper.classList.remove(CLASSNAME.SEARCHBAR_WRAPPER_VISIBLE_SUGGESTIONS);
  suggestLayer.style.display = "none";
  suggestLayer.innerHTML = "";
  renderCache.clear();
  oldSearchTerm = undefined;
}

/**
 * Used by jasmine tests
 */
export function reset() {
  if (!searchField) {
    throw new Error("searchSuggest.reset: searchField is not defined");
  }
  if (!searchbarWrapper) {
    throw new Error("searchSuggest.reset: searchbarWrapper is not defined");
  }
  if (!suggestLayer) {
    throw new Error("searchSuggest.reset: suggestLayer is not defined");
  }
  if (!renderCache) {
    throw new Error("searchSuggest.reset: renderCache is not defined");
  }
  searchField.value = "";
  hideLayer();
  searchbarWrapper = null;
  searchForm = null;
  searchField = null;
  suggestLayer = null;
  oldSearchTerm = "";
  renderCache.clear();
}

export function init() {
  headline = HEADLINE.SUGGESTIONS;

  const parentWrapper: HTMLDivElement = document.querySelector(SELECTOR.SEARCH_CONTAINER) as HTMLDivElement;

  renderCache = new Cache<Promise<string>>(retrieveSuggestions);
  if (!searchForm && parentWrapper) {
    searchbarWrapper = parentWrapper.querySelector(SELECTOR.SEARCH_WRAPPER) as HTMLDivElement;
    searchForm = parentWrapper.querySelector(SELECTOR.SEARCH_FORM) as HTMLFormElement;
    searchField = parentWrapper.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    suggestLayer = parentWrapper.querySelector(SELECTOR.LAYER) as HTMLDivElement;
    headerElement = document.querySelector(SELECTOR.HEADER_ELEMENT) as HTMLElement;

    // be safe during experiment
    if (suggestLayer) {
      window.o_util.event.delegate(SELECTOR.LAYER, "click", SELECTOR.SUGGEST_TERM, clickSearchTermAction);
      window.o_util.event.delegate(SELECTOR.LAYER, "click", SELECTOR.MODIFIED_ARROW, clickArrowAction);

      searchField.addEventListener("keyup", handleKeyAction);
      searchField.addEventListener("focus", focusSearchFieldAction);
      searchField.addEventListener("focusout", focusOutSearchFieldAction);
    }
  }
}

export const searchSuggest = { init, reset, setOldSearchTerm };
